import { useCallback } from 'react';
import { useToast } from 'hooks/useToast';
import { useAppSelector, useAppDispatch } from 'store/hooks/useStore';
import { ReleaseTask } from 'types/releaseTask';
import * as releaseApi from 'api/releaseApi';
import {
  listSelectors,
  listActions,
} from 'store/reducers/releaseReducers/releaseTaskReducer/listSlice';
import { editActions } from 'store/reducers/releaseReducers/releaseTaskReducer/editSlice';

/**
 * リリースタスク一覧 store の実装をタスクから隠すための中間層
 * @returns selector と action のラッパー
 */
export const useListStore = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();

  const loading = useAppSelector(listSelectors.loading);
  const releaseTasks = useAppSelector(listSelectors.releaseTasks);

  const fetchReleaseTasks = useCallback(async () => {
    dispatch(listActions.loadStarted());
    const taskResult = await releaseApi.fetchReleaseTasks();
    const groupResult = await releaseApi.fetchTargetGroup();
    const environmentResult = await releaseApi.fetchReleaseEnvironments();
    if (!taskResult.ok) {
      toast({
        status: 'error',
        description: `タスク一覧の取得に失敗しました: ${taskResult.err.message}`,
      });
    } else if (!groupResult.ok) {
      toast({
        status: 'error',
        description: `ターゲットグループ一覧の取得に失敗しました: ${groupResult.err.message}`,
      });
    } else if (!environmentResult.ok) {
      toast({
        status: 'error',
        description: `環境一覧の取得に失敗しました: ${environmentResult.err.message}`,
      });
    } else {
      dispatch(
        listActions.fetchTargetGrouopSuccess({
          targetGroupList: groupResult.value,
        }),
      );
      dispatch(
        listActions.fetchReleaseTaskSuccess({
          releaseTasks: taskResult.value,
        }),
      );
      dispatch(
        listActions.fetchReleaseEnvironmentSuccess({
          releaseEnvironments: environmentResult.value,
        }),
      );
    }
    dispatch(listActions.loadEnded());
  }, [dispatch, toast]);

  const onOpenModal = useCallback(
    (releaseTask: ReleaseTask) => {
      dispatch(editActions.modalOpened(releaseTask));
    },
    [dispatch],
  );

  return {
    /** 一覧ロード状態 */
    loading,
    /** リリースタスク一覧データ */
    releaseTasks,
    /** リリースタスク一覧を取得 */
    fetchReleaseTasks,
    /** 詳細を表示 */
    onOpenModal,
  };
};
